import { getApiUrl } from './configService';

// Default API URL to use until the configuration is loaded
let BASE_URL = process.env.NODE_ENV === 'development' ? '' : 'http://localhost:5001';

// Initialize the API URL
(async () => {
  try {
    BASE_URL = await getApiUrl();
    console.log('Auth Service initialized with URL:', BASE_URL);
  } catch (error) {
    console.error('Failed to initialize Auth URL:', error);
    console.log('Using default Auth URL:', BASE_URL);
  }
})();

// Helper function to get the current API URL
// This is needed because the BASE_URL might be updated after initialization
const getCurrentApiUrl = async () => {
  // Always return /api/auth for development to use the proxy
  console.log('getCurrentApiUrl called, NODE_ENV:', process.env.NODE_ENV);
  return '/api/auth';
};

/**
 * Authenticate a user with username and password
 * @param {String} username - Username
 * @param {String} password - Password
 * @returns {Promise<Object>} - User object and token
 * @throws {Error} - If authentication fails
 */
export const login = async (username, password, rememberMe = false) => {
  try {
    console.log('Attempting login with username:', username);

    // Validate inputs
    if (!username || !password) {
      console.error('Login error: Missing username or password');
      throw new Error('Username and password are required');
    }

    // Get the current API URL
    const apiUrl = await getCurrentApiUrl();

    // Prepare request body
    const requestBody = JSON.stringify({ username, password, rememberMe });
    console.log('Request body prepared (length):', requestBody.length);
    
    // Make the request
    console.log('Sending login request to:', `${apiUrl}/login`);
    const response = await fetch(`${apiUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: requestBody
    });
    
    console.log('Login response status:', response.status);
    console.log('Login response status text:', response.statusText);
    
    // Check if response is ok before trying to parse JSON
    if (!response.ok) {
      console.error('Login response not OK:', response.status, response.statusText);
      
      // Try to parse error message from JSON if possible
      try {
        const errorText = await response.text();
        console.log('Error response text:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
          console.error('Parsed error data:', errorData);
          const loginErr = new Error(errorData.message || `Authentication failed with status: ${response.status}`);
          if (errorData.code) loginErr.code = errorData.code;
          if (errorData.submittedAt) loginErr.submittedAt = errorData.submittedAt;
          throw loginErr;
        } catch (jsonParseError) {
          console.error('Error parsing error response JSON:', jsonParseError);
          throw new Error(`Authentication failed with status: ${response.status} ${response.statusText}`);
        }
      } catch (textError) {
        console.error('Error getting response text:', textError);
        throw new Error(`Authentication failed with status: ${response.status} ${response.statusText}`);
      }
    }
    
    // Get response as text first
    const text = await response.text();
    console.log('Response text length:', text.length);
    console.log('Response text preview:', text.substring(0, 100) + '...');
    
    if (!text) {
      console.error('Empty response from server');
      throw new Error('Empty response from server');
    }
    
    // Parse JSON safely
    let data;
    try {
      data = JSON.parse(text);
      console.log('Login response parsed successfully');
    } catch (jsonError) {
      console.error('JSON parse error:', jsonError, 'Response text:', text);
      throw new Error('Invalid JSON response from server');
    }
    
    console.log('Login successful, received data:', data.success ? 'success' : 'failure');
    
    if (!data.token || !data.user) {
      console.error('Invalid authentication response:', data);
      throw new Error('Invalid authentication response (missing token or user data)');
    }
    
    // Store token in localStorage
    console.log('Storing token and user data in localStorage');
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (rememberMe) {
      localStorage.setItem('rememberMe', '1');
    } else {
      localStorage.removeItem('rememberMe');
    }
    
    console.log('Login completed successfully for user:', data.user.username);
    return data.user;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Log out the current user (clear local state; optionally notify server to clear cookie).
 * Uses a full-page redirect to the public dashboard (/) so logout works reliably and avoids
 * "The operation is insecure" that can occur when ProtectedRoute calls navigate() after auth state is cleared.
 */
export const isRememberMe = () => localStorage.getItem('rememberMe') === '1';

export const logout = () => {
  console.log('Logging out user');
  const token = localStorage.getItem('token');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('rememberMe');
  if (token) {
    getCurrentApiUrl().then(apiUrl => {
      fetch(`${apiUrl}/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
    }).catch(() => {});
  }
  console.log('User logged out successfully');
  // Full-page redirect to public dashboard (not login)
  window.location.replace('/');
};

/**
 * Get the current authenticated user
 * @returns {Object|null} - User object or null if not authenticated
 */
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    console.log('No user found in localStorage');
    return null;
  }
  
  try {
    const user = JSON.parse(userStr);
    console.log('Current user from localStorage:', user.username);
    return user;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Check if a user is authenticated
 * @returns {Boolean} - True if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const user = getCurrentUser();
  if (!token || !user) {
    console.log('Authentication check: Not authenticated (no token or user)');
    return false;
  }

  // Decode JWT payload to check expiry without a server round-trip
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      console.log('Authentication check: Token expired — clearing local session');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberMe');
      return false;
    }
  } catch {
    console.log('Authentication check: Invalid token format — clearing local session');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return false;
  }

  console.log('Authentication check: Authenticated');
  return true;
};

/**
 * Check if the current user has a specific role
 * @param {String} role - Role to check
 * @returns {Boolean} - True if the user has the role, false otherwise
 */
export const hasRole = (role) => {
  const user = getCurrentUser();
  const hasUserRole = user && user.role === role;
  console.log(`Role check for '${role}':`, hasUserRole ? 'Has role' : 'Does not have role');
  return hasUserRole;
};

/**
 * Check if the current user is an admin (includes SuperAdmin for admin-level access)
 * @returns {Boolean} - True if the user is an admin or superadmin, false otherwise
 */
export const isAdmin = () => {
  const user = getCurrentUser();
  const isUserAdmin = user && (user.role === 'admin' || user.role === 'superadmin');
  console.log('Admin check:', isUserAdmin ? 'Is admin' : 'Is not admin');
  return isUserAdmin;
};

/**
 * Check if the current user is a SuperAdmin (role assigned via DB only, not shown in UI)
 * @returns {Boolean} - True if the user is a superadmin, false otherwise
 */
export const isSuperAdmin = () => {
  const user = getCurrentUser();
  const isUserSuperAdmin = user && user.role === 'superadmin';
  console.log('SuperAdmin check:', isUserSuperAdmin ? 'Is SuperAdmin' : 'Is not SuperAdmin');
  return isUserSuperAdmin;
};

export const hasOperationalExcellenceAccess = () => getCurrentUser()?.operationalExcellenceAccess === true;

/** The leadership briefing needs Operational Excellence access plus the Briefing toggle (granted in User Management). */
export const hasBriefingAccess = () => {
  const user = getCurrentUser();
  return user?.operationalExcellenceAccess === true && user?.briefingAccess === true;
};

/** Admins, or users granted the Survey admin toggle, can start, stop and reopen team surveys. */
export const canManageSurveys = () => {
  const user = getCurrentUser();
  return !!user && (user.role === 'admin' || user.role === 'superadmin' || user.surveyAdminAccess === true);
};

/**
 * Check if the current user has a specific permission
 * @param {String} permission - Permission to check
 * @returns {Boolean} - True if the user has the permission, false otherwise
 */
export const hasPermission = (permission) => {
  const user = getCurrentUser();
  if (!user) return false;
  
  // Permission mapping based on roles
  const rolePermissions = {
    'superadmin': ['*'], // SuperAdmin has all permissions (assigned via DB only)
    'admin': ['*'], // Admin has all permissions
    'engineer': [
      'workitems:read', 
      'performance:read', 
      'qa:read', 
      'qa:write'
    ],
    'dev_manager': [
      'workitems:read', 
      'workitems:write', 
      'performance:read',
      'workitems:admin'
    ],
    'qa_manager': [
      'workitems:read', 
      'qa:read', 
      'qa:write', 
      'qa:admin', 
      'performance:read',
      'performance:write',
      'performance:delete',
      'ado:fetch'
    ]
  };
  
  const userPermissions = rolePermissions[user.role] || [];
  const hasUserPermission = userPermissions.includes('*') || userPermissions.includes(permission);
  console.log(`Permission check for '${permission}':`, hasUserPermission ? 'Has permission' : 'Does not have permission');
  return hasUserPermission;
};

/**
 * Check if the current user is a manager (dev_manager or qa_manager)
 * @returns {Boolean} - True if the user is a manager, false otherwise
 */
export const isManager = () => {
  const user = getCurrentUser();
  const isUserManager = user && (user.role === 'dev_manager' || user.role === 'qa_manager');
  console.log('Manager check:', isUserManager ? 'Is manager' : 'Is not manager');
  return isUserManager;
};

/**
 * Check if the current user is a QA manager
 * @returns {Boolean} - True if the user is a QA manager, false otherwise
 */
export const isQAManager = () => {
  const user = getCurrentUser();
  const isUserQAManager = user && user.role === 'qa_manager';
  console.log('QA Manager check:', isUserQAManager ? 'Is QA manager' : 'Is not QA manager');
  return isUserQAManager;
};

/**
 * Check if the current user is a Dev manager
 * @returns {Boolean} - True if the user is a Dev manager, false otherwise
 */
export const isDevManager = () => {
  const user = getCurrentUser();
  const isUserDevManager = user && user.role === 'dev_manager';
  console.log('Dev Manager check:', isUserDevManager ? 'Is Dev manager' : 'Is not Dev manager');
  return isUserDevManager;
};

/**
 * Check if the current user can perform QA manager actions (QA Manager or Admin)
 * @returns {Boolean} - True if the user can perform QA manager actions, false otherwise
 */
export const canPerformQAActions = () => {
  const user = getCurrentUser();
  if (!user) return false;
  const canPerform = user.role === 'admin' || user.role === 'superadmin' || user.role === 'qa_manager';
  console.log('QA Actions check:', canPerform ? 'Can perform QA actions' : 'Cannot perform QA actions');
  return canPerform;
};

/**
 * Get the current user's profile from the API
 * @returns {Promise<Object>} - User profile
 * @throws {Error} - If the request fails
 */
export const getProfile = async () => {
  try {
    console.log('Getting user profile');
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found');
      throw new Error('No authentication token found');
    }
    
    // Get the current API URL
    const apiUrl = await getCurrentApiUrl();
    
    console.log('Sending profile request to:', `${apiUrl}/me`);
    const response = await fetch(`${apiUrl}/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('Profile response status:', response.status);
    
    if (!response.ok) {
      console.error('Profile response not OK:', response.status, response.statusText);
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get user profile');
    }
    
    const data = await response.json();
    console.log('Profile data received successfully');
    
    return data.user;
  } catch (error) {
    console.error('Error getting profile:', error);
    throw new Error(error.message || 'Failed to get user profile');
  }
};

/**
 * Register a new user
 * @param {Object} userData - User data including username, email, password, displayName, and role
 * @returns {Promise<Object>} - Registered user data
 * @throws {Error} - If registration fails
 */
export const register = async (userData) => {
  try {
    console.log('Registering new user:', userData.username);
    
    // Get the current API URL
    const apiUrl = await getCurrentApiUrl();
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('You must be logged in as an admin to register new users.');
    }
    
    const response = await fetch(`${apiUrl}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(userData)
    });
    
    console.log('Registration response status:', response.status);
    
    if (!response.ok) {
      console.error('Registration response not OK:', response.status, response.statusText);
      const errorData = await response.json();
      throw new Error(errorData.message || 'Registration failed');
    }
    
    const data = await response.json();
    console.log('Registration successful for user:', userData.username);
    
    return data;
  } catch (error) {
    console.error('Registration error:', error);
    throw new Error(error.message || 'Registration failed');
  }
};

/**
 * Reset a user's password (admin only)
 * @param {String} userId - User ID
 * @param {String} newPassword - New password
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If password reset fails
 */
export const resetPassword = async (userId, newPassword) => {
  try {
    console.log('Resetting password for user ID:', userId);
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    
    if (!token) {
      console.error('No authentication token found for password reset');
      throw new Error('Authentication required');
    }
    
    // Get the current API URL
    const baseUrl = await getCurrentApiUrl();
    
    // Make the request
    const response = await fetch(`${baseUrl}/reset-password/${userId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ newPassword })
    });
    
    console.log('Password reset response status:', response.status);
    
    if (!response.ok) {
      console.error('Password reset response not OK:', response.status, response.statusText);
      const errorData = await response.json();
      throw new Error(errorData.message || 'Password reset failed');
    }
    
    const data = await response.json();
    console.log('Password reset successful');
    
    return data;
  } catch (error) {
    console.error('Password reset error:', error);
    throw new Error(error.message || 'Password reset failed');
  }
};

/**
 * Reset password directly using username, email, and new password
 * @param {String} username - Username
 * @param {String} email - User's email address
 * @param {String} newPassword - New password
 * @returns {Promise<Object>} - Response data
 * @throws {Error} - If password reset fails
 */
export const resetPasswordDirect = async (username, email, newPassword) => {
  try {
    console.log('Direct password reset for username:', username);
    
    // Get the current API URL
    const apiUrl = await getCurrentApiUrl();
    
    const response = await fetch(`${apiUrl}/reset-password-direct`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, email, newPassword })
    });
    
    console.log('Direct password reset response status:', response.status);
    
    if (!response.ok) {
      console.error('Direct password reset response not OK:', response.status, response.statusText);
      const errorData = await response.json();
      throw new Error(errorData.message || 'Password reset failed');
    }
    
    const data = await response.json();
    console.log('Direct password reset successful');
    
    return data;
  } catch (error) {
    console.error('Direct password reset error:', error);
    throw new Error(error.message || 'Password reset failed');
  }
};
